import React, { useState } from 'react';
import { Campaign } from '../types';

interface CampaignFormProps {
  initial?: Partial<Campaign>;
  onSave: (data: Omit<Campaign, 'id'>) => Promise<void>;
  onCancel: () => void;
  saving: boolean;
}

const TOPIC_OPTIONS = [
  'technology', 'startups', 'finance', 'health', 'food',
  'travel', 'sports', 'music', 'gaming', 'automotive',
  'fashion', 'real estate', 'education', 'entertainment',
];

const CampaignForm: React.FC<CampaignFormProps> = ({ initial = {}, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({
    advertiser_name: initial.advertiser_name || '',
    sbic_offer_text: initial.sbic_offer_text || '',
    sbic_discount_code: initial.sbic_discount_code || '',
    advertiser_url: initial.advertiser_url || '',
    topic_categories: initial.topic_categories || '',
    target_keywords: (initial as any).target_keywords || '',
    active: initial.active ?? true,
    bid_rate_usd: initial.bid_rate_usd ?? 0.05,
    sbic_expiry_hours: initial.sbic_expiry_hours ?? 24,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const selectedTopics = form.topic_categories
    ? form.topic_categories.split(',').map(t => t.trim()).filter(Boolean)
    : [];

  const toggleTopic = (topic: string) => {
    const current = new Set(selectedTopics);
    if (current.has(topic)) current.delete(topic);
    else current.add(topic);
    setForm(f => ({ ...f, topic_categories: Array.from(current).join(',') }));
  };

  const validate = () => {
    const e: Record<string, string> = {};
    if (!form.advertiser_name.trim()) e.advertiser_name = 'Required';
    if (!form.sbic_offer_text.trim()) e.sbic_offer_text = 'Required';
    if (!form.sbic_discount_code.trim()) e.sbic_discount_code = 'Required';
    if (!form.advertiser_url.trim()) e.advertiser_url = 'Required';
    if (form.bid_rate_usd <= 0) e.bid_rate_usd = 'Must be > 0';
    if (form.sbic_expiry_hours <= 0) e.sbic_expiry_hours = 'Must be > 0';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSave(form);
  };

  const field = (label: string, key: keyof typeof form, type = 'text', extra?: React.InputHTMLAttributes<HTMLInputElement>) => (
    <div style={s.field}>
      <label style={s.label}>{label}</label>
      <input
        type={type}
        value={String(form[key])}
        onChange={e => setForm(f => ({ ...f, [key]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
        style={{ ...s.input, ...(errors[key] ? s.inputError : {}) }}
        {...extra}
      />
      {errors[key] && <span style={s.errMsg}>{errors[key]}</span>}
    </div>
  );

  return (
    <form onSubmit={handleSubmit} style={s.form}>
      <div style={s.grid2}>
        {field('Advertiser Name', 'advertiser_name', 'text', { placeholder: 'e.g. Apple, Inc.' })}
        {field('Advertiser URL', 'advertiser_url', 'url', { placeholder: 'https://...' })}
      </div>
      {field('Offer Text', 'sbic_offer_text', 'text', { placeholder: 'e.g. Get 20% off AirPods' })}
      <div style={s.grid2}>
        {field('Discount Code', 'sbic_discount_code', 'text', { placeholder: 'e.g. FIC20OFF' })}
        <div style={s.field}>
          <label style={s.label}>Status</label>
          <div
            onClick={() => setForm(f => ({ ...f, active: !f.active }))}
            style={{ ...s.toggle, background: form.active ? '#f0fdf4' : '#f9fafb', borderColor: form.active ? '#16a34a' : '#e5e7eb' }}
          >
            <div style={{ ...s.toggleDot, background: form.active ? '#16a34a' : '#d1d5db', transform: form.active ? 'translateX(22px)' : 'translateX(2px)' }} />
            <span style={{ ...s.toggleLabel, color: form.active ? '#16a34a' : '#6b7280' }}>
              {form.active ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>
      <div style={s.grid2}>
        {field('Bid Rate (USD per impression)', 'bid_rate_usd', 'number', { step: '0.001', min: '0.001' })}
        {field('Offer Expiry (hours)', 'sbic_expiry_hours', 'number', { step: '1', min: '1' })}
      </div>
      <div style={s.field}>
        <label style={s.label}>Topic Categories</label>
        <div style={s.topicGrid}>
          {TOPIC_OPTIONS.map(t => (
            <button
              key={t}
              type="button"
              onClick={() => toggleTopic(t)}
              style={{ ...s.topicBtn, ...(selectedTopics.includes(t) ? s.topicBtnActive : {}) }}
            >
              {t}
            </button>
          ))}
        </div>
      </div>
      <div style={s.field}>
        <label style={s.label}>Target Keywords</label>
        <input
          type="text"
          value={form.target_keywords}
          onChange={e => setForm(f => ({ ...f, target_keywords: e.target.value }))}
          style={s.input}
          placeholder="e.g. AI startups, fundraising, SaaS (comma separated)"
        />
        <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
          Comma-separated keywords matched against riders' custom session descriptions for higher-precision targeting.
        </span>
      </div>
      <div style={s.actions}>
        <button type="button" onClick={onCancel} style={s.btnSecondary}>Cancel</button>
        <button type="submit" disabled={saving} style={{ ...s.btnPrimary, opacity: saving ? 0.6 : 1 }}>
          {saving ? 'Saving…' : 'Save Campaign'}
        </button>
      </div>
    </form>
  );
};

const s: Record<string, React.CSSProperties> = {
  form: { display: 'flex', flexDirection: 'column', gap: 20 },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { color: '#374151', fontSize: 13, fontWeight: 500 },
  input: {
    background: '#f9fafb',
    border: '1.5px solid #e5e7eb',
    borderRadius: 8,
    color: '#111827',
    padding: '11px 14px',
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    outline: 'none',
  },
  inputError: { borderColor: '#dc2626' },
  errMsg: { color: '#dc2626', fontSize: 11 },
  toggle: {
    display: 'flex', alignItems: 'center', gap: 10,
    border: '1.5px solid', borderRadius: 8, padding: '8px 14px',
    cursor: 'pointer', height: 44, boxSizing: 'border-box',
    transition: 'all 0.2s',
  },
  toggleDot: {
    width: 18, height: 18, borderRadius: '50%',
    transition: 'all 0.25s', flexShrink: 0,
  },
  toggleLabel: { fontSize: 14, fontWeight: 600, fontFamily: "'Inter', sans-serif" },
  topicGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  topicBtn: {
    background: '#f9fafb', border: '1.5px solid #e5e7eb', borderRadius: 20,
    color: '#6b7280', padding: '6px 14px', fontSize: 12, cursor: 'pointer',
    fontFamily: "'Inter', sans-serif", transition: 'all 0.15s', fontWeight: 500,
  },
  topicBtnActive: {
    background: '#f0fdf4', borderColor: '#16a34a', color: '#16a34a',
  },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: 12, paddingTop: 8, borderTop: '1px solid #f3f4f6', marginTop: 4 },
  btnSecondary: {
    background: 'transparent', border: '1.5px solid #e5e7eb', borderRadius: 8,
    color: '#6b7280', padding: '10px 20px', fontSize: 13, cursor: 'pointer',
    fontFamily: "'Inter', sans-serif", fontWeight: 500,
  },
  btnPrimary: {
    background: '#111827', border: 'none', borderRadius: 8,
    color: '#ffffff', padding: '10px 24px', fontSize: 13,
    fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif",
  },
};

export default CampaignForm;